import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { CurrencyCode } from '@/shared/lib/currency';

import { BalloonResult, BalloonState, BalloonStatus } from './types';

const initialState: BalloonState = {
  status: BalloonStatus.IDLE,
  betAmount: null,
  currency: null,
  serverSeedHash: null,
  multiplier: 1.0,
  pauseRemainingMs: null,
  result: null,
  loading: false,
};

const balloonSlice = createSlice({
  name: 'balloon',
  initialState,
  reducers: {
    // ── User-triggered actions (sagas / wsSaga listens) ──────────────────
    startGame(_state, _action: PayloadAction<{ betAmount: number; currency: CurrencyCode; giftId?: string }>) {},
    heatStart(_state) {},
    heatStop(_state) {},
    take(_state) {},
    resetGame(state) {
      Object.assign(state, initialState);
    },

    // ── Internal state mutations ─────────────────────────────────────────
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    /** Called by wsSaga on every balloon_state message from server */
    wsUpdate(
      state,
      action: PayloadAction<{
        status: string;
        multiplier: number;
        pauseRemainingMs: number | null;
        betAmount?: number | null;
        currency?: string | null;
        serverSeedHash?: string | null;
        cashoutMultiplier?: number | null;
        payout?: number | null;
        crashPoint?: number | null;
        serverSeed?: string | null;
        clientSeed?: string | null;
      }>,
    ) {
      const d = action.payload;
      state.status = d.status as BalloonStatus;
      state.multiplier = d.multiplier;
      state.pauseRemainingMs = d.pauseRemainingMs;
      state.loading = false;

      if (d.betAmount != null) state.betAmount = d.betAmount;
      if (d.currency != null) state.currency = d.currency;
      if (d.serverSeedHash != null) state.serverSeedHash = d.serverSeedHash;

      if (d.status === BalloonStatus.IDLE) {
        Object.assign(state, initialState);
        return;
      }

      // Populate result fields when game ends
      if (
        (d.status === BalloonStatus.WON || d.status === BalloonStatus.BUSTED) &&
        d.crashPoint != null
      ) {
        state.result = {
          cashoutMultiplier: d.cashoutMultiplier ?? d.multiplier,
          payout: d.payout ?? 0,
          crashPoint: d.crashPoint,
          serverSeed: d.serverSeed ?? '',
          clientSeed: d.clientSeed ?? '',
        };
      }
    },

    /** Signal-only: lucky save granted for this round (no state change — BalloonVisual uses a ref) */
    wsBalloonLucky(_state) {},

    /** Server-side error (INSUFFICIENT_BALANCE, etc.) */
    wsError(state, _action: PayloadAction<{ code: string; message: string }>) {
      state.loading = false;
    },

    /** Final state: won (called by wsSaga after balance update) */
    setResult(state, action: PayloadAction<BalloonResult>) {
      state.status = BalloonStatus.WON;
      state.result = action.payload;
      state.loading = false;
    },
  },
});

export const balloonActions = balloonSlice.actions;
export const balloonReducer = balloonSlice.reducer;
